import {omit} from 'lodash'
import {t} from '../engine'
import {
  IEntityModel,
  IQuestionnaire,
  IQuestionnaireDisplayLogic,
  IQuestionnaireDisplayLogicType,
  IQuestionnaireDisplayRuleCondition,
  IQuestionnaireDisplayRuleConditionNumber,
  IQuestionnaireDisplayRuleConditionOption,
  IQuestionnaireDisplayRuleConditionType,
  IQuestionnaireDisplayRuleSource,
  IQuestionnaireDisplayRuleSourceQuestion,
  IQuestionnaireDisplayRuleSourceType,
  IQuestionnaireDisplayRuleTarget,
  IQuestionnaireDisplayRuleTargetQuestion,
  IQuestionnaireDisplayRuleTargetSection,
  IQuestionnaireDisplayRuleTargetSubmit,
  IQuestionnaireDisplayRuleTargetType,
  IQuestionnaireDisplayStrategy,
  IQuestionnaireQuestion,
  IQuestionnaireSection,
  QuestionnaireQuestionType,
  SelectInputConfig,
} from '../shared/db'
import {
  QuestionnaireTempSection,
  QuestionnaireTempQuestion,
  QuestionnaireTempInputConfigOptions,
  TempQuestionnaire,
} from './task'

export interface UpsertDisplayRule {
  source?:
    | (Omit<IQuestionnaireDisplayRuleSource, 'displayRuleId' | 'type' | keyof IEntityModel> & {
        type: IQuestionnaireDisplayRuleSource['type']
      })
    | null
  condition?:
    | (Pick<IQuestionnaireDisplayRuleCondition, 'operator'> & {
        type: IQuestionnaireDisplayRuleCondition['type']
        inputNumberList?: Omit<
          IQuestionnaireDisplayRuleConditionNumber['inputNumberList'][number],
          'displayRuleConditionId' | keyof IEntityModel
        >[]
        inputOptionList?: Omit<
          IQuestionnaireDisplayRuleConditionOption['inputOptionList'][number],
          'displayRuleConditionId' | keyof IEntityModel
        >[]
      })
    | null
  target?:
    | (Omit<IQuestionnaireDisplayRuleTarget, 'displayRuleId' | 'type' | keyof IEntityModel> & {
        type: IQuestionnaireDisplayRuleTarget['type']
      })
    | null
}

export type UpsertDisplayLogic = Required<Pick<IQuestionnaireDisplayLogic, 'type'>> & {
  displayRuleList?: UpsertDisplayRule[]
}

export interface UpsertDisplayStrategy {
  displayLogicList?: UpsertDisplayLogic[]
}

export interface UpsertDisplayStrategyMapItem {
  contentIndex: number
  displayStrategy: UpsertDisplayStrategy
}

export type QuestionnaireDisplayStrategyUpsertItem = (
  | {
      sectionId: string
    }
  | {
      questionId: string
    }
  | {
      displayStrategyId: string
    }
) & {
  displayStrategy: UpsertDisplayStrategy
}

export type TempQuestionnaireDisplayStrategyUpsertItem = {
  sectionId?: string
  questionId?: string
  displayStrategyId?: string
  displayStrategy: UpsertDisplayStrategy
}

export const fetchDisplayStrategyListFromQuestionnaire = (questionnaire: IQuestionnaire | TempQuestionnaire) => {
  return questionnaire.sectionList
    .flatMap((section) => [section, ...(section.questionList ?? [])])
    .filter((content) => (content as IQuestionnaireSection | IQuestionnaireQuestion).displayStrategyId)
    .map((content) => {
      const result: TempQuestionnaireDisplayStrategyUpsertItem = {
        displayStrategyId: content.displayStrategyId,
        displayStrategy: parseDisplayStrategy(content.displayStrategy),
      }
      if ('type' in content) {
        result.questionId = content.id
      } else {
        result.sectionId = content.id
      }
      return result
    })
}

const parseDisplayStrategy = (
  displayStrategy?: IQuestionnaireDisplayStrategy | UpsertDisplayStrategy | null,
): UpsertDisplayStrategy => {
  if (!displayStrategy) return {}
  return {
    displayLogicList: displayStrategy.displayLogicList?.map((logic) => {
      return {
        type: logic.type,
        displayRuleList: logic.displayRuleList?.map(({source, condition, target}) => {
          const result: UpsertDisplayRule = {}
          if (source) {
            const {type, questionnaireQuestionId, questionnaireSectionId} = source
            if (type === IQuestionnaireDisplayRuleSourceType.Question) {
              result.source = {type, questionnaireQuestionId}
            } else {
              result.source = {type, questionnaireSectionId}
            }
          }
          if (condition) {
            const {type, inputNumberList, inputOptionList, operator} = condition
            if (type === IQuestionnaireDisplayRuleConditionType.Number) {
              result.condition = {
                type,
                inputNumberList: inputNumberList?.map(({value, operator}) => ({
                  value,
                  operator,
                })),
                operator,
              }
            } else if (type === IQuestionnaireDisplayRuleConditionType.Option) {
              result.condition = {
                type,
                inputOptionList: inputOptionList?.map(({optionId}) => ({
                  optionId,
                })),
                operator,
              }
            }
          }
          if (target) {
            const {type, questionnaireSectionId, questionnaireQuestionId} = target
            if (type === IQuestionnaireDisplayRuleTargetType.Submit) {
              result.target = {type}
            } else if (type === IQuestionnaireDisplayRuleTargetType.Section) {
              result.target = {type, questionnaireSectionId}
            } else if (type === IQuestionnaireDisplayRuleTargetType.Question) {
              result.target = {type, questionnaireQuestionId}
            }
          }
          return result
        }),
      }
    }),
  }
}

const sanitizeDuplicatedQuestion = (question: IQuestionnaireQuestion): QuestionnaireTempQuestion => {
  const result: QuestionnaireTempQuestion = {
    id: question.id,
    label: t.uuid(),
    type: question.type || QuestionnaireQuestionType.MultiSelect,
    description: question.description || 'New Question',
    required: question.required || false,
    displayStrategyId: question.displayStrategyId,
    displayStrategy: question.displayStrategy,
    mediaUrl: question.mediaUrl,
  }
  switch (question.type) {
    case QuestionnaireQuestionType.Select:
    case QuestionnaireQuestionType.MultiSelect:
    case QuestionnaireQuestionType.Radio:
      result.inputConfig = {
        ...question.inputConfig,
        options: (question.inputConfig as SelectInputConfig).options.map(({name, value}) => ({
          name: name || 'New Option',
          value: value,
        })),
      }
      break
    case QuestionnaireQuestionType.Number:
      result.inputConfig = question.inputConfig
      break
  }
  return result
}

export const sanitizeDuplicatedSection = (section: IQuestionnaireSection): QuestionnaireTempSection => {
  return {
    id: section.id,
    legend: section.legend || 'New Section Title',
    index: section.index || 0,
    description: section.description || '',
    questionList: section.questionList?.map(sanitizeDuplicatedQuestion) ?? [],
    displayStrategyId: section.displayStrategyId,
    displayStrategy: section.displayStrategy,
  }
}

export const sanitizeTempSection = (section: IQuestionnaireSection | QuestionnaireTempSection) => {
  // remove unnecessary properties
  return {
    ...omit(section, ['id', 'questionList', 'displayStrategyId', 'displayStrategy']),
    questionList:
      section.questionList?.map((question) => {
        if (
          [
            QuestionnaireQuestionType.Select,
            QuestionnaireQuestionType.MultiSelect,
            QuestionnaireQuestionType.Radio,
          ].includes(question.type)
        ) {
          const inputConfig = (question.inputConfig as SelectInputConfig) || {options: []}
          return {
            ...omit(question, ['id', 'displayStrategyId', 'displayStrategy']),
            inputConfig: {
              ...inputConfig,
              options: (inputConfig.options ?? []).map(({name}) => ({name})),
            },
          }
        }
        return {
          ...omit(question, ['id', 'displayStrategyId', 'displayStrategy']),
        }
      }) ?? [],
  }
}

export const sanitizeQuestionnaireForUpdate = (questionnaire: IQuestionnaire) => {
  return {
    ...questionnaire,
    sectionList: questionnaire.sectionList.map(({id, displayStrategy, questionList, ...rest}) => {
      return {
        ...rest,
        questionList: questionList?.map(({id, displayStrategy, type, inputConfig, ...rest}) => {
          {
            if (
              [
                QuestionnaireQuestionType.Select,
                QuestionnaireQuestionType.MultiSelect,
                QuestionnaireQuestionType.Radio,
              ].includes(type)
            ) {
              return {
                ...rest,
                type,
                questionnaireId: questionnaire.id,
                inputConfig: {
                  ...inputConfig,
                  options: (inputConfig as SelectInputConfig).options.map(({name}) => ({
                    name,
                  })),
                },
              }
            }
            return {
              ...rest,
              type,
              inputConfig,
              questionnaireId: questionnaire.id,
            }
          }
        }),
      }
    }),
  }
}

export const createDisplayStrategyUpsertListMap = (
  questionnaireSectionList?: IQuestionnaireSection[] | QuestionnaireTempSection[],
  displayStrategyList?: TempQuestionnaireDisplayStrategyUpsertItem[],
): UpsertDisplayStrategyMapItem[] => {
  if (!questionnaireSectionList || !displayStrategyList) return []

  const contentList = questionnaireSectionList.flatMap((section) => [section, ...(section.questionList ?? [])]) ?? []
  return displayStrategyList
    .map((item) => {
      return {
        contentIndex:
          contentList.findIndex(({id}) => {
            return id === item.sectionId || id === item.questionId
          }) ?? -1,
        displayStrategy: {
          displayLogicList:
            item.displayStrategy.displayLogicList?.map(({displayRuleList, type}) => {
              if (type === IQuestionnaireDisplayLogicType.Display) {
                return {
                  type,
                  displayRuleList: displayRuleList?.map((rule) => {
                    const condition = rule.condition as
                      | IQuestionnaireDisplayRuleConditionNumber
                      | IQuestionnaireDisplayRuleConditionOption
                    const source = rule.source as IQuestionnaireDisplayRuleSourceQuestion
                    if (condition?.type === IQuestionnaireDisplayRuleConditionType.Option) {
                      const sourceQuestion = contentList.find(({id}) => id === source.questionnaireQuestionId) as
                        | QuestionnaireTempQuestion
                        | IQuestionnaireQuestion
                      return {
                        source: {
                          ...source,
                          questionnaireQuestionId: contentList
                            .findIndex(({id}) => id === source.questionnaireQuestionId)
                            .toString(),
                        },
                        condition: {
                          ...condition,
                          inputOptionList: condition.inputOptionList.map(({optionId, ...rest}) => ({
                            ...rest,
                            optionId:
                              (sourceQuestion?.inputConfig as QuestionnaireTempInputConfigOptions).options
                                ?.findIndex(({value}) => value === optionId)
                                .toString() ?? '-1',
                          })),
                        },
                      }
                    }
                    return {
                      ...rule,
                      source: {
                        ...source,
                        questionnaireQuestionId: contentList
                          .findIndex(({id}) => id === source.questionnaireQuestionId)
                          .toString(),
                      },
                    }
                  }),
                }
              } else if (type === IQuestionnaireDisplayLogicType.Skip) {
                return {
                  type,
                  displayRuleList: displayRuleList?.map((rule) => {
                    const condition = rule.condition as
                      | IQuestionnaireDisplayRuleConditionNumber
                      | IQuestionnaireDisplayRuleConditionOption
                    const target = rule.target as
                      | IQuestionnaireDisplayRuleTargetQuestion
                      | IQuestionnaireDisplayRuleTargetSection
                      | IQuestionnaireDisplayRuleTargetSubmit
                    const newTarget =
                      target.type === IQuestionnaireDisplayRuleTargetType.Submit
                        ? {...target}
                        : target.type === IQuestionnaireDisplayRuleTargetType.Section
                          ? {
                              ...target,
                              questionnaireSectionId: contentList
                                .findIndex(({id}) => id === target.questionnaireSectionId)
                                .toString(),
                            }
                          : {
                              ...target,
                              questionnaireQuestionId: contentList
                                .findIndex(({id}) => id === target.questionnaireQuestionId)
                                .toString(),
                            }
                    if (condition?.type === IQuestionnaireDisplayRuleConditionType.Option) {
                      const sourceQuestion = contentList.find(({id}) => id === item.questionId) as
                        | QuestionnaireTempQuestion
                        | IQuestionnaireQuestion
                      return {
                        condition: {
                          ...condition,
                          inputOptionList: condition.inputOptionList.map(({optionId, ...rest}) => {
                            return {
                              ...rest,
                              optionId:
                                (sourceQuestion?.inputConfig as QuestionnaireTempInputConfigOptions).options
                                  ?.findIndex(({value}) => value === optionId)
                                  .toString() ?? '-1',
                            }
                          }),
                        },
                        target: newTarget,
                      }
                    }
                    return {
                      ...rule,
                      target: newTarget,
                    }
                  }),
                }
              }
              return {type, displayRuleList}
            }) || [],
        },
      }
    })
    .filter((item) => item !== undefined)
}

export const parseDisplayStrategyUpsertListFromMap = (
  questionnaireSectionList?: IQuestionnaireSection[] | QuestionnaireTempSection[],
  upsertDisplayStrategyMapItemList?: UpsertDisplayStrategyMapItem[],
) => {
  if (!questionnaireSectionList || !upsertDisplayStrategyMapItemList) return []

  const contentList = questionnaireSectionList.flatMap((section) => [section, ...(section.questionList ?? [])])

  return upsertDisplayStrategyMapItemList.map((item) => {
    const contentItem = contentList[item.contentIndex]
    const result: {
      sectionId?: string
      questionId?: string
      displayStrategy: UpsertDisplayStrategy
    } = {
      displayStrategy: {
        displayLogicList:
          item.displayStrategy.displayLogicList?.map(({displayRuleList, type}) => {
            if (type === IQuestionnaireDisplayLogicType.Display) {
              return {
                type,
                displayRuleList: displayRuleList?.map((rule) => {
                  const source = rule.source as IQuestionnaireDisplayRuleSourceQuestion
                  const condition = rule.condition as
                    | IQuestionnaireDisplayRuleConditionNumber
                    | IQuestionnaireDisplayRuleConditionOption
                  if (condition?.type === IQuestionnaireDisplayRuleConditionType.Option) {
                    const sourceQuestion = contentList[
                      parseInt(source.questionnaireQuestionId)
                    ] as IQuestionnaireQuestion
                    return {
                      source: {
                        ...source,
                        questionnaireQuestionId: sourceQuestion.id,
                      },
                      condition: {
                        ...condition,
                        inputOptionList: condition.inputOptionList.map(({optionId, ...rest}) => {
                          return {
                            ...rest,
                            optionId: (sourceQuestion.inputConfig as SelectInputConfig).options[parseInt(optionId)]
                              ?.value,
                          }
                        }),
                      },
                    }
                  }
                  return {
                    source: {
                      ...source,
                      questionnaireQuestionId: contentList[parseInt(source.questionnaireQuestionId)].id,
                    },
                    condition,
                  }
                }),
              }
            } else if (type === IQuestionnaireDisplayLogicType.Skip) {
              return {
                type,
                displayRuleList: displayRuleList
                  ?.filter((rule) => {
                    const target = rule.target as
                      | IQuestionnaireDisplayRuleTargetQuestion
                      | IQuestionnaireDisplayRuleTargetSection
                      | IQuestionnaireDisplayRuleTargetSubmit
                    if (target.type === IQuestionnaireDisplayRuleTargetType.Submit) {
                      return true
                    } else if (target.type === IQuestionnaireDisplayRuleTargetType.Section) {
                      return target.questionnaireSectionId !== '-1'
                    }
                    return target.questionnaireQuestionId !== '-1'
                  })
                  .map((rule) => {
                    const condition = rule.condition as
                      | IQuestionnaireDisplayRuleConditionNumber
                      | IQuestionnaireDisplayRuleConditionOption
                    const target = rule.target as
                      | IQuestionnaireDisplayRuleTargetQuestion
                      | IQuestionnaireDisplayRuleTargetSection
                      | IQuestionnaireDisplayRuleTargetSubmit
                    const newTarget =
                      target.type === IQuestionnaireDisplayRuleTargetType.Submit
                        ? target
                        : target.type === IQuestionnaireDisplayRuleTargetType.Section
                          ? {
                              ...target,
                              questionnaireSectionId: contentList[parseInt(target.questionnaireSectionId)].id,
                            }
                          : {
                              ...target,
                              questionnaireQuestionId: contentList[parseInt(target.questionnaireQuestionId)].id,
                            }
                    if (condition?.type === IQuestionnaireDisplayRuleConditionType.Option) {
                      return {
                        condition: {
                          ...condition,
                          inputOptionList: condition.inputOptionList.map(({optionId, ...rest}) => {
                            return {
                              ...rest,
                              optionId: ((contentItem as IQuestionnaireQuestion).inputConfig as SelectInputConfig)
                                .options[parseInt(optionId)]?.value,
                            }
                          }),
                        },
                        target: newTarget,
                      }
                    }
                    return {
                      condition,
                      target: newTarget,
                    }
                  }),
              }
            }
            return {type, displayRuleList}
          }) || [],
      },
    }
    if ('questionnaireSectionId' in contentItem) {
      result.questionId = contentItem.id
    } else {
      result.sectionId = contentItem.id
    }
    return result
  })
}
